import { useTranslation } from 'next-i18next';
import React, { ReactElement } from 'react';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import {
  FdrProductFeatureGroup,
  FdrRequestButton,
} from '@hotelplan/supergraph-api';
import { useLayoutContext } from 'components/domain/layout/Layout.context';
import {
  ClickEventSourceType,
  trackRequestFormClick,
} from 'components/domain/tracking/tracking.utils';
import {
  FdrFeatureBlock,
  FdrHotelDescriptionColumn,
  FdrHotelDescriptionContainer,
  FdrPdpDescriptionContainer,
  FdrPdpDescriptionLeadColumn,
  FdrPdpDescriptionRequestButton,
  FdrPdpDescriptionRouteColumn,
  FdrPdpDescriptionWrapper,
  FdrPdpLeadText,
} from './fdr-pdp.styles';
import { checkProductType } from './fdr-pdp.utils';

interface IFdrPdpDescriptionProps {
  leadText: string;
  routes?: Array<string>;
  featureGroups?: Array<FdrProductFeatureGroup>;
  staticRequestButton: FdrRequestButton;
}

export function FdrPdpDescription(
  props: IFdrPdpDescriptionProps
): ReactElement {
  const { id } = useObjectId();
  const { headerHeight } = useLayoutContext();

  const { isHotel } = checkProductType(id);

  return (
    <FdrPdpDescriptionWrapper
      id="pdp-description-block"
      headerStickyWrapper={headerHeight}
      className={isHotel ? 'is-hotel' : null}
    >
      {isHotel ? (
        <FdrHotelDescription {...props} />
      ) : (
        <FdrRegularDescription {...props} />
      )}
    </FdrPdpDescriptionWrapper>
  );
}

function FdrHotelDescription({
  leadText,
  staticRequestButton,
}: IFdrPdpDescriptionProps): ReactElement {
  return (
    <FdrHotelDescriptionContainer>
      <FdrHotelDescriptionColumn>
        <FdrPdpLeadText
          className="is-hotel"
          dangerouslySetInnerHTML={{ __html: leadText }}
        />
        <FdrPdpDescriptionRequestButton
          link={staticRequestButton?.link}
          caption={staticRequestButton?.objectCaption}
          backgroundColor="greenLight"
          onClick={() => {
            trackRequestFormClick(ClickEventSourceType.DESCRIPTION);
          }}
        />
      </FdrHotelDescriptionColumn>
    </FdrHotelDescriptionContainer>
  );
}

function FdrRegularDescription({
  leadText,
  routes,
  featureGroups,
  staticRequestButton,
}: IFdrPdpDescriptionProps): ReactElement {
  const [t] = useTranslation('pdp');

  return (
    <FdrPdpDescriptionContainer>
      <FdrPdpDescriptionLeadColumn>
        <FdrPdpLeadText dangerouslySetInnerHTML={{ __html: leadText }} />
        <FdrPdpDescriptionRequestButton
          link={staticRequestButton.link}
          caption={staticRequestButton.objectCaption}
          backgroundColor="paperGrey"
          onClick={() => {
            trackRequestFormClick(ClickEventSourceType.DESCRIPTION);
          }}
        />
      </FdrPdpDescriptionLeadColumn>
      <FdrPdpDescriptionRouteColumn>
        <div className="features-wrapper">
          {routes?.length ? (
            <FdrFeatureBlock>
              <span className="feature-name">{t('pdp:route.title')}</span>
              {routes.map(function (route, index) {
                return (
                  <span key={`route-${index}`}>{`${route}${
                    index !== routes.length - 1 ? ' - ' : ''
                  }`}</span>
                );
              })}
            </FdrFeatureBlock>
          ) : null}
          {featureGroups?.length ? (
            <>
              {featureGroups?.map(function (group) {
                return (
                  <FdrFeatureBlock key={`product-feature-${group.id}`}>
                    <span className="feature-name">{group.name}</span>
                    {group.features
                      .filter(feature => feature.enabled)
                      .map(function (feature, index, filteredFeatures) {
                        return (
                          <span key={feature.id}>{`${feature.name}${
                            index + 1 !== filteredFeatures.length ? ', ' : ''
                          }`}</span>
                        );
                      })}
                  </FdrFeatureBlock>
                );
              })}
            </>
          ) : null}
        </div>
      </FdrPdpDescriptionRouteColumn>
    </FdrPdpDescriptionContainer>
  );
}
